import { z } from '../zod';
import {
  HostedLayoutTypeInputValidationSchema,
  LocaleCodeEnumValidationSchema,
  PageStatusEnumValidationSchema,
} from './enum';
import { ThemeOverridesInputValidationSchema } from '../theme/themeOverrides';
import { FieldInputValidationSchema } from '../field/create';
import { CreatePageFormLayoutInputValidationSchema } from './formLayout';
import { RedirectAndConfirmationFieldsInputValidationSchema } from './common/redirectAndConfirmation';
import { NotificationFieldsInputValidationSchema } from './common/notification';
import { PageInvoiceSettingsInputValidationSchema } from './common/invoiceSettings';
import { CreateFunnelStepInputValidationSchema } from './common/funnel';
import { PagePaymentMethodsInputValidationSchema } from './common/paymentMethods';
import { PageCheckoutAbandonmentSettingsInputValidationSchema } from './common/abandonmentSettings';
import {
  hasSupportedEnabledPaymentOptionCombination,
  PagePaymentOptionInputValidationSchema,
  SUPPORTED_PAYMENT_OPTION_COMBINATIONS_DESCRIPTION,
  UNSUPPORTED_PAYMENT_OPTION_COMBINATION_MESSAGE,
} from './common/paymentOptions';
import { PageFeeInputValidationSchema } from './common/fee';
import { PageCoverProcessingFeesInputValidationSchema } from './common/coverProcessingFees';
import { PageThemeIdInputValidationSchema } from './common/themeId';
import { isReservedSlug } from './reservedSlugs';

const BasePageCoreFieldsInputValidationSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(500)
    .meta({
      description: 'Internal page name used in your dashboard and API responses.',
      examples: ['Premium Course Checkout'],
    }),
  slug: z
    .string()
    .min(1)
    .max(200)
    .nullish()
    .meta({
      description:
        'Page URL slug. For events and forms the slug defaults to the slugified version of your `title`. For checkouts the slug defaults to the slugified version of your `productData.title`.',
      example: 'premium-course-checkout',
    })
    .refine(slug => slug == null || slug === slug.toLowerCase(), {
      message: 'Slug needs to be lowercase',
    })
    .refine(slug => slug == null || !/\s/.test(slug), {
      message: "Slug can't contain spaces. Use dashes (-) instead?",
    })
    .refine(slug => slug == null || !slug.startsWith('/'), {
      message: "Slug can't start with a slash",
    })
    .refine(slug => slug == null || !isReservedSlug(slug), {
      message: "This slug is reserved and can't be used",
    }),
  locale: LocaleCodeEnumValidationSchema.nullish(),
  customizeCheckoutConfirmation: z.boolean().nullish().meta({
    description:
      'If set to true `confirmationCheckoutMessage` and `confirmationCheckoutTitle` will be displayed on the confirmation page. Defaults to `false`.',
  }),
  status: PageStatusEnumValidationSchema.nullish(),
  testmode: z.boolean().optional().meta({
    description:
      'Puts the page in test mode. While it is on, every purchase on this page is a test purchase: no money is taken, the buyer still receives everything the page delivers, and the records are created with `livemode: false`. Turn it off to take real payments again.',
  }),
  fields: z.array(FieldInputValidationSchema).nullish().meta({
    description:
      'Custom checkout fields to create. When provided, at least one field must be an `email` element with `type` `email` and `required` `true`. When omitted, the page gets Name and Email address fields, plus a required billing Address on checkout and event pages. Forms get Name and Email address only.',
  }),
  formLayout: CreatePageFormLayoutInputValidationSchema.optional(),
  allowDynamicTitle: z.boolean().nullish().meta({
    description: 'Allow page title to be overridden via URL query parameters.',
    example: false,
  }),
  allowDynamicDescription: z.boolean().nullish().meta({
    description: 'Allow page description to be overridden via URL query parameters.',
    example: false,
  }),
  closePopupOnClickOutside: z.boolean().nullish().meta({
    description:
      'Whether a popup embed closes when clicking outside the container. Defaults to `false`.',
    example: false,
  }),
  hostedLayoutType: HostedLayoutTypeInputValidationSchema.nullish(),
  themeId: PageThemeIdInputValidationSchema,
  themeOverrides: ThemeOverridesInputValidationSchema.nullish(),
});

export const BasePageFieldsInputValidationSchema = BasePageCoreFieldsInputValidationSchema.extend(
  RedirectAndConfirmationFieldsInputValidationSchema.shape,
)
  .extend(NotificationFieldsInputValidationSchema.shape)
  .extend({
    invoiceSettings: PageInvoiceSettingsInputValidationSchema.nullish(),
    funnelSteps: z
      .array(CreateFunnelStepInputValidationSchema)
      .nullish()
      .meta({
        description:
          'Ordered funnel configuration for this page. Supports intermediate checkout steps, one-click upsells, and confirmation behavior.',
        examples: [
          [
            {
              type: 'upsell',
              order: 1,
              enabled: true,
              config: {
                action: 'checkout',
                pageId: '507f1f77bcf86cd799439012',
              },
            },
            {
              type: 'confirmation',
              order: 2,
              enabled: true,
              config: {
                action: 'confirmation',
                customizeCheckoutConfirmation: true,
                confirmationCheckoutTitle: 'Order confirmed',
                confirmationCheckoutMessage: '<p>Thanks for your purchase.</p>',
              },
            },
          ],
        ],
      }),
  });

export const BaseCheckoutAndEventFieldsInputValidationSchema = z.object({
  savePaymentMethod: z.boolean().nullish().openapi({
    description:
      "Whether to save the customer's payment method after payment. Defaults to false when omitted. Subscription payment methods are always saved.",
    example: false,
  }),
  paymentMethods: PagePaymentMethodsInputValidationSchema.nullish(),
  checkoutAbandonment: PageCheckoutAbandonmentSettingsInputValidationSchema.nullish(),
  paymentOptions: z
    .array(PagePaymentOptionInputValidationSchema)
    .nullish()
    .superRefine((paymentOptions, ctx) => {
      if (!hasSupportedEnabledPaymentOptionCombination(paymentOptions)) {
        ctx.addIssue({
          code: 'custom',
          message: UNSUPPORTED_PAYMENT_OPTION_COMBINATION_MESSAGE,
        });
      }
    })
    .openapi({
      description:
        'Payment options presented to customers at checkout. Only these enabled combinations are supported: ' +
        SUPPORTED_PAYMENT_OPTION_COMBINATIONS_DESCRIPTION +
        '. Combine `full` (pay now), `manual` (invoice or cash on delivery), and `partial` (deposit) types to offer flexible payment flows. At least one option should have `enabled: true`. ' +
        '[Learn more](https://checkoutpage.com/docs/articles/manual-payments-pay-in-full-by-invoice-pay-deposit-or-cash-on-delivery)',
      example: [
        {
          type: 'full',
          enabled: true,
          name: 'Pay in full',
          description: 'Pay the full amount now',
          instructions: '',
          partialAmount: null,
          showPaymentButton: true,
        },
        {
          type: 'manual',
          enabled: false,
          name: 'Pay via invoice',
          description: 'Payment due in 30 days',
          instructions: 'Bank details will be sent via email after checkout.',
          showPaymentButton: true,
          manualType: 'invoice',
        },
        {
          type: 'partial',
          enabled: false,
          name: 'Pay a deposit',
          description: 'Pay a deposit now and the rest later',
          instructions: 'Pay the rest of your balance at a later date.',
          partialAmount: 5000,
          showPaymentButton: true,
        },
        {
          type: 'manual',
          enabled: false,
          name: 'Cash on delivery',
          description: 'Pay when you receive your order',
          instructions: 'Pay the full amount when your order is delivered.',
          partialAmount: null,
          showPaymentButton: true,
          manualType: 'cash_on_delivery',
        },
      ],
    }),
  fees: z
    .array(PageFeeInputValidationSchema)
    .nullish()
    .openapi({
      description:
        'Add extra fees at checkout, like processing or handling. [Learn more](https://checkoutpage.com/docs/articles/fees-and-surcharges)',
      example: [
        {
          name: 'Processing fee',
          amount: 1000,
          multiplyByQuantity: false,
          applyToSpecificPaymentMethods: false,
          paymentMethods: [],
        },
      ],
    }),
  coverProcessingFees: PageCoverProcessingFeesInputValidationSchema,
  showCouponCodeField: z.boolean().nullish().openapi({
    description: 'Whether to show the coupon code input.',
    example: false,
  }),
  // Database does not support null.
  showCouponCodeFieldType: z.enum(['link', 'field']).optional().openapi({
    description: 'Display type for coupon code field.',
    example: 'link',
  }),
  allowDynamicPrice: z.boolean().nullish().openapi({
    description: 'Allow price to be overridden via URL query parameters.',
    example: false,
  }),
  allowDynamicDiscountedFromPrice: z.boolean().nullish().openapi({
    description:
      'Allow the compare-at (discounted-from) price to be overridden via URL query parameters.',
    example: false,
  }),
  allowDynamicLabel: z.boolean().nullish().openapi({
    description: "Allow a price's label to be overridden via URL query parameters.",
    example: false,
  }),
  allowDynamicPreselectedPrice: z.boolean().nullish().openapi({
    description:
      'Allow a price to be preselected (and revealed if hidden) via URL query parameters.',
    example: false,
  }),
  allowDynamicRedirectUrl: z.boolean().nullish().openapi({
    description: 'Allow redirect URL to be overridden via URL query parameters.',
    example: false,
  }),
});
